import { FC, useCallback, useEffect, useRef } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import {
  Box,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Text,
} from '@hopskipdrive/react-components';
import { Home, Star } from '@mui/icons-material';
import { colorPalette } from '@rideiq/shared/components/variables';

import { CustomReport } from '../../../types';

import { Container, NewReportSx } from './CustomReportListPanel.styles';

export interface CustomReportListPanelProps {
  items: CustomReport[];
  itemCount: number;
  selectedId: string;
  handleClick: (data) => void;
}

export const CustomReportListPanel: FC<CustomReportListPanelProps> = ({
  items,
  itemCount,
  selectedId,
  handleClick,
}) => {
  const listRef = useRef<VariableSizeList>(null);
  const sizeMap = useRef({});
  const setSize = useCallback((index, size) => {
    sizeMap.current = { ...sizeMap.current, [index]: size };
    listRef.current?.resetAfterIndex(index);
  }, []);
  const getSize = (index) => sizeMap.current[index];

  return (
    <Container>
      <Text sx={NewReportSx} onClick={() => handleClick(null)}>
        New Report
      </Text>
      <Divider />
      <Box sx={{ width: '100%', height: 400, maxWidth: 220, bgcolor: 'background.paper' }}>
        <VariableSizeList
          ref={listRef}
          height={400}
          width="100%"
          itemSize={getSize}
          itemCount={itemCount}
          overscanCount={5}
          itemData={items}
        >
          {(props) => RenderRow({ ...props, selectedId, handleClick, setSize })}
        </VariableSizeList>
      </Box>
    </Container>
  );
};

function RenderRow(props: ListChildComponentProps) {
  const textElementRef = useRef<HTMLSpanElement>(null);
  const { index, style, data, selectedId, handleClick, setSize } = props;

  useEffect(() => {
    setSize(index, textElementRef.current?.parentElement?.clientHeight);
  }, [setSize, index]);

  return (
    <ListItem style={style} key={index} component="div" disablePadding>
      <ListItemButton selected={selectedId === data[index].uuid} onClick={() => handleClick(data[index])}>
        <ListItemText primary={data[index].name} ref={textElementRef} />

        <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
          {data[index].default && <Home htmlColor={colorPalette.neutral80} />}
          {data[index].favorite && <Star sx={{ ml: '.5rem' }} htmlColor={colorPalette.neutral80} />}
        </ListItemIcon>
      </ListItemButton>
    </ListItem>
  );
}
